import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages.ts";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";
import { wacToken } from "@/libraries/design/foundations";
import { useLocale } from "@/libraries/web-framework";

const containerStyles = css({
    display: "flex",
    height: "128px",
    justifyContent: "center",
    flexShrink: 0,
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#F8F8F8",
    position: "relative",
    insetBlockStart: -60,
    alignItems: "flex-end",
    paddingBlockEnd: 32,
    [media.only.md]: {
        alignItems: "flex-start",
        flexDirection: "column",
        flexWrap: "wrap",
        height: "207px",
        paddingBlockEnd: 0
    },
    [media.only.sm]: {
        alignItems: "flex-start",
        flexDirection: "column",
        flexWrap: "wrap",
        height: "207px",
        paddingBlockEnd: 0
    },
    [media.below.sm]: {
        alignItems: "flex-start",
        flexDirection: "column",
        flexWrap: "wrap",
        height: "250px",
        paddingBlockEnd: 0
    }
});

const additionalCategoriesTextStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#101214",
    textAlign: "center",
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: "28px",
    marginInlineEnd: "50px",
    [media.only.md]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    },
    [media.only.sm]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    },
    [media.below.sm]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    }
});

const categoryList = css({
    display: "flex",
    flexDirection: "row",
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "14px",
    fontWeight: 400,
    letterSpacing: 0.42,
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#101214",
    gap: 64,
    wordWrap: "break-word",
    justifyContent: "space-between",
    margin: 0,
    zIndex: 2,
    [media.only.md]: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        rowGap: 8,
        columnGap: 128
    },
    [media.only.sm]: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        rowGap: 8,
        columnGap: 128
    },
    [media.below.sm]: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        rowGap: 8,
        columnGap: 64
    }
});
const categoryListItem = css({
    listStyleType: "none",
    lineHeight: "28px"
});

export interface AdditionalCategoriesSectionProps {
    categories: {
        id: string;
        name: string;
        urlSlug: string;
    }[];
}

const localizedLinkForCategory = (locale: string, categoryId: string) => {
    const prefix = locale === "en" ? "" : `/${locale}`;
    if (categoryId === "recommended") {
        return `${prefix}/software/confluence/templates`;
    }
    return `${prefix}/software/confluence/templates/categories/${categoryId}`;
};

const AdditionalCategoriesSection = ({
    categories
}: AdditionalCategoriesSectionProps) => {
    const locale = useLocale();

    return (
        <div css={containerStyles}>
            <div css={additionalCategoriesTextStyles}>
                <FormattedMessage
                    {...ConfluenceTemplateMessages.additionalCategoriesText}
                />
            </div>
            <ul css={categoryList}>
                {categories.map((category) => (
                    <li key={category.id} css={[categoryListItem]}>
                        <a
                            href={localizedLinkForCategory(
                                locale,
                                category.urlSlug
                            )}
                            {...ConfluenceTemplateAnalytics.viewTemplateCategory(
                                category.urlSlug
                            )}
                        >
                            {category.name}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};
AdditionalCategoriesSection.displayName = "AdditionalCategoriesSection";
export { AdditionalCategoriesSection };
